import classNames from 'classnames';

import React, {
  Children,
  createElement,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './segmented-control.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    children?: ReadonlyArray<ReactElement>;
    small?: boolean;
    onChange?: (value: string) => void;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-segmented-control';

export const SegmentedControl: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  ({ className, small = false, children, onChange, ...rest }, ref, dataTestId = COMPONENT_ID) => {
    const [selectedSegment, setSelectedSegment] = useState<string>();
    const childArray = useMemo(
      () => Children.toArray(children) as ReadonlyArray<ReactElement>,
      [children],
    );
    const highlightRef = useRef<HTMLDivElement>(null);
    const classes = classNames(styles.container, className, {
      [styles.small]: small,
    });

    const hasDivider = (child: ReactElement, idx: number, array: ReadonlyArray<ReactElement>) => {
      const first = idx === 0;
      const checkedIndex = array.findIndex(
        ({ props }: ReactElement) => props.value === selectedSegment,
      );

      return !(first || idx === checkedIndex || idx === checkedIndex + 1);
    };

    const handleChange = (value: string) => {
      setSelectedSegment(value);
      onChange?.(value);
    };

    useEffect(() => {
      if (childArray.length > 0) {
        const checkedChildValue = childArray.find(
          ({ props }: ReactElement) => props?.checked,
        ) as ReactElement;
        if (checkedChildValue) {
          setSelectedSegment(checkedChildValue?.props?.value);
        } else {
          setSelectedSegment(childArray[0].props.value);
        }
      }
    }, [childArray]);

    useEffect(() => {
      const width = 100 / childArray.length;
      const selectedIndex = childArray.findIndex(
        (child: ReactElement) => child.props.value === selectedSegment,
      );
      const left = selectedIndex * width;
      const { style: highlightStyle } = highlightRef.current;

      highlightStyle.setProperty('width', `${width}%`);
      highlightStyle.setProperty('left', `${left}%`);
    }, [childArray, selectedSegment]);

    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        className={classes}
        {...rest}>
        <div className={styles.highlight} ref={highlightRef} />
        {childArray.map((child, idx, array) =>
          createElement(child.type, {
            ...child.props,
            small,
            key: child.props.value,
            onChange: handleChange,
            divider: hasDivider(child, idx, array),
            checked: selectedSegment === child.props.value,
          }),
        )}
      </div>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';
